import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { Box, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useForm } from "react-hook-form";
import AddIcon from "@mui/icons-material/Add";
import ReceiptIcon from "@mui/icons-material/Receipt";
import {
  CustomSectionTitle,
  CustomSummaryCard,
  CustomButton,
  CustomModal,
  CustomTable,
  MobileTable,
  CustomColumnDef,
  CardTab,
  CustomTextInput,
  CustomSingleSelect,
  CustomSelect,
  colors,
  type CategoryOption,
} from "@ippon/shared-components";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import {
  fetchPaymentsByProperty,
  updatePayment,
  deletePayment,
} from "@/store/paymentSlice";
import { fetchBudgetCategories } from "@/store/budgetSlice";
import {
  fetchPaymentOptionManagement,
  upsertPaymentOptionManagement,
} from "@/store/paymentOptionManagementSlice";
import { useGlobalToast } from "@/hooks/useGlobalToast";
import { Can } from "@/components/auth/Can";
import ipponApi from "@/config/axios";
import { buildFiscalYearOptions } from "@/utils/fiscalYearOptions";
import dayjs from "dayjs";
import { fmt } from "./shared";
import { DEFAULT_STATUSES } from "../shared/PaymentDrawer";
import IncomeCreateDrawer from "../shared/IncomeCreateDrawer";
import PaymentDetailDrawer from "../shared/PaymentDetailDrawer";

const BaseURL = import.meta.env.VITE_API_URL;

const ALL_TAB_KEY = "all";

const TableEllipsisText: React.FC<{ text: string }> = ({ text }) => (
  <Box
    component="span"
    data-table-ellipsis
    sx={{
      display: "block",
      width: "100%",
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }}
  >
    {text}
  </Box>
);

/**
 * 収入科目の上位カテゴリ (revenue_group)。Unit.revenue_type と 1:1。
 * 収入サブタブの「カテゴリ」列・CardTab はこの 3 区分で表示する (§5.3)。
 */
const REVENUE_GROUPS: ReadonlyArray<{ key: string; label: string }> = [
  { key: "stock_rent", label: "貸室" },
  { key: "stock_other", label: "付帯" },
  { key: "other", label: "その他" },
];

const REVENUE_GROUP_LABEL: Record<string, string> = {
  stock_rent: "貸室",
  stock_other: "付帯",
  other: "その他",
};

interface IncomeListTabProps {
  propertyId: string;
  propertyName?: string;
}

interface IncomeItem {
  name: string;
  amount: number;
}

type PerPageForm = { perPage: number };

const PER_PAGE_OPTIONS = [
  { value: 10, label: "10件" },
  { value: 25, label: "25件" },
  { value: 50, label: "50件" },
  { value: 100, label: "100件" },
];

/** PaymentRecord (BE) を一覧行に整形した型 */
interface IncomeRow {
  id: string;
  payment_id: string;
  sort_key: string;
  unit_id: string;
  unit_name: string;
  revenue_group: string; // stock_rent / stock_other / other / ""
  category_label: string;
  /** テナント名 (契約者名)。Unit→active 契約から解決。その他 (Unit 非紐付け) は空。 */
  tenant_name: string;
  /** 表示用タイトル。BE の title 優先、未設定なら "YYYY年MM月入金" にフォールバック。 */
  title: string;
  billing_month: string; // "YYYY-MM"
  status: string;
  amount: number; // 請求額 = items 合計
  /** 入金額。案C(receipts)未実装のため暫定で status 由来 (入金済=請求額満額 / それ以外=null)。 */
  paid_amount: number | null;
  scheduled_date: string; // 支払期日
  transfer_datetime: string; // 入金日
  created_at: string;
  items: IncomeItem[];
}

/** "YYYY-MM" → "YYYY年MM月" (不正値は空文字)。 */
const formatBillingMonth = (m: string): string =>
  m && /^\d{4}-\d{2}$/.test(m) ? `${m.slice(0, 4)}年${m.slice(5, 7)}月` : "";

const IncomeListTab: React.FC<IncomeListTabProps> = ({
  propertyId,
  propertyName,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { showToast } = useGlobalToast();

  const propertyPayments = useSelector(
    (s: RootState) => (s as any).payments?.propertyList?.data || [],
  );
  const loading = useSelector(
    (s: RootState) => (s as any).payments?.propertyList?.loading || false,
  );
  const [summaryLoading, setSummaryLoading] = useState(loading);
  const effectiveSummaryLoading = loading || summaryLoading;

  // 収入科目 (BudgetCategory income) を起点に category_id → revenue_group を引く
  const budgetCategories = useSelector(
    (s: RootState) => (s as any).budgets?.categories?.data || [],
  );
  const budgetCategoriesLoading = useSelector(
    (s: RootState) => (s as any).budgets?.categories?.loading || false,
  );
  useEffect(() => {
    if (budgetCategories.length === 0 && !budgetCategoriesLoading) {
      dispatch(fetchBudgetCategories() as any);
    }
  }, [dispatch, budgetCategories.length, budgetCategoriesLoading]);

  const categoryRevenueGroup = useMemo(() => {
    const map: Record<string, string> = {};
    (budgetCategories as any[]).forEach((c) => {
      if (c && c.category_type === "income" && c.revenue_group) {
        map[c.category_id] = c.revenue_group;
      }
    });
    return map;
  }, [budgetCategories]);

  // データ取得 (物件全体)
  useEffect(() => {
    if (!propertyId) return;
    dispatch(fetchPaymentsByProperty({ propertyId }) as any);
  }, [dispatch, propertyId]);

  // テナント名 (契約者名) 解決用: 物件配下の active 契約を取得し unit_id → contractor_name を引く。
  // (RentRoll と同じ機構。収入一覧の「テナント名」列で使う。その他=Unit 非紐付けは空。)
  const [tenantByUnitId, setTenantByUnitId] = useState<Record<string, string>>(
    {},
  );
  useEffect(() => {
    if (!propertyId) return;
    let cancelled = false;
    ipponApi
      .get(`${BaseURL}/api/v1/contracts`, {
        params: { property_id: propertyId, status: "active" },
      })
      .then((res) => {
        if (cancelled) return;
        const items = (res.data?.data?.items || []) as any[];
        const byUnit: Record<string, any> = {};
        for (const c of items) {
          const uid = c?.unit_id;
          if (!uid) continue;
          const prev = byUnit[uid];
          if (
            !prev ||
            new Date(c.created_at ?? 0).getTime() >
              new Date(prev.created_at ?? 0).getTime()
          ) {
            byUnit[uid] = c;
          }
        }
        const map: Record<string, string> = {};
        Object.keys(byUnit).forEach((uid) => {
          map[uid] = String(byUnit[uid]?.contractor_name || "");
        });
        setTenantByUnitId(map);
      })
      .catch(() => setTenantByUnitId({}));
    return () => {
      cancelled = true;
    };
  }, [propertyId]);

  // ── ステータスのインライン更新 (入金管理ページ=PaymentHistoryTab と同じ挙動) ──
  // 支払ステータス選択肢: DEFAULT_STATUSES + paymentOptionManagement のユーザー追加分
  const optionRecord = useSelector(
    (s: RootState) => (s as any).paymentOptionManagement?.record,
  );
  const [statusOptions, setStatusOptions] =
    useState<CategoryOption[]>(DEFAULT_STATUSES);
  useEffect(() => {
    dispatch(fetchPaymentOptionManagement() as any)
      .unwrap()
      .then((rec: any) => {
        if (rec && Array.isArray(rec.statuses)) {
          const merged = Array.from(
            new Set([...DEFAULT_STATUSES.map((o) => o.value), ...rec.statuses]),
          );
          setStatusOptions(
            merged.map((v) => ({ label: String(v), value: String(v) })),
          );
        }
      })
      .catch(() => {});
  }, [dispatch]);
  useEffect(() => {
    if (!optionRecord || !Array.isArray(optionRecord.statuses)) return;
    const merged = Array.from(
      new Set([
        ...DEFAULT_STATUSES.map((o) => o.value),
        ...optionRecord.statuses,
      ]),
    );
    setStatusOptions(
      merged.map((v) => ({ label: String(v), value: String(v) })),
    );
  }, [optionRecord]);

  // payment_id → status の楽観的マップ (select の即時反映用)。サーバ再取得で同期。
  const [statusMap, setStatusMap] = useState<Record<string, string>>({});
  const handleInlineStatusChange = useCallback(
    async (
      paymentId: string,
      unitId: string,
      sortKey: string,
      newStatus: string,
    ) => {
      const previous = statusMap[paymentId];
      if (previous === newStatus) return;
      setStatusMap((prev) => ({ ...prev, [paymentId]: newStatus }));
      try {
        // 更新は client_id + sort_key で引かれる。URL の section は unit 紐付けなら
        // unit_id、物件直入金 (その他=unit_id 不在) は propertyId を path に渡す。
        const updateBody: any = {
          propertySectionId: unitId || propertyId,
          paymentId,
          sort_key: sortKey,
          status: newStatus,
        };
        if (newStatus === "入金済") {
          updateBody.transfer_datetime = new Date().toISOString();
        }
        await dispatch(updatePayment(updateBody) as any).unwrap();
        // ローカル追加ステータスの遅延 upsert (PaymentHistoryTab と同じ)
        const isDefault = DEFAULT_STATUSES.some((d) => d.value === newStatus);
        const beStatuses: string[] = Array.isArray(optionRecord?.statuses)
          ? optionRecord.statuses
          : [];
        if (!isDefault && !beStatuses.includes(newStatus)) {
          dispatch(
            upsertPaymentOptionManagement({
              existingId: optionRecord?.payment_option_management_id,
              statuses: [...beStatuses, newStatus],
              payment_items: optionRecord?.payment_items || [],
            }) as any,
          ).catch(() => {});
        }
        // 支出一覧 (ExpenseListTab) と同じく再フェッチしない。
        // 楽観 statusMap で即時反映済。サーバ値は次回の一覧取得時に同期される。
        showToast({ type: "success", message: "ステータスを更新しました" });
      } catch {
        setStatusMap((prev) => ({ ...prev, [paymentId]: previous }));
        showToast({ type: "error", message: "ステータスの更新に失敗しました" });
      }
    },
    [statusMap, dispatch, propertyId, optionRecord, showToast],
  );

  // CardTab: revenue_group での絞り込み (本タブは URL 同期しない。支出の ?category= と衝突回避)。
  // 収入登録モーダルの初期カテゴリにも使うため、モーダル state より前で宣言する。
  const [selectedGroup, setSelectedGroup] = useState<string>(ALL_TAB_KEY);

  // ── 収入登録モーダル (IncomeCreateDrawer に抽出。収入タブ / 予実表「その他」で共用) ──
  const [createOpen, setCreateOpen] = useState(false);

  const [searchQuery, setSearchQuery] = useState("");
  const [perPage, setPerPage] = useState(50);
  const { control: perPageControl } = useForm<PerPageForm>({
    defaultValues: { perPage: 50 },
  });

  const {
    register: registerFilter,
    control: controlFilter,
    formState: { errors: filterErrors },
    getValues: getFilterValues,
    reset: resetFilter,
    setValue: setFilterValue,
  } = useForm<{ q: string; fiscalYear: string }>({
    defaultValues: { q: "", fiscalYear: "" },
  });

  // 会計年度: BE は決算月 (fiscal_closing_month) を返す。会計年度開始月はその翌月。
  // (支出 ExpenseListTab / Budget と同じロジックに揃える)
  const fiscalClosingMonth: number =
    useSelector(
      (state: RootState) =>
        (state.clients?.detailed?.data as { fiscal_closing_month?: number })
          ?.fiscal_closing_month,
    ) ?? 3;
  const fiscalYearStartMonth: number =
    fiscalClosingMonth === 12 ? 1 : fiscalClosingMonth + 1;
  const currentFiscalYear = useMemo(() => {
    const now = new Date();
    return now.getMonth() + 1 >= fiscalYearStartMonth
      ? now.getFullYear()
      : now.getFullYear() - 1;
  }, [fiscalYearStartMonth]);

  const [selectedFiscalYear, setSelectedFiscalYear] =
    useState<number>(currentFiscalYear);
  // currentFiscalYear が後追いで確定したとき (fiscal_closing_month 遅延ロード) は初期同期。
  // ユーザーが既に手動選択していたら触らない。
  const fiscalYearUserTouchedRef = useRef(false);
  useEffect(() => {
    if (fiscalYearUserTouchedRef.current) return;
    setSelectedFiscalYear(currentFiscalYear);
    setFilterValue("fiscalYear", String(currentFiscalYear));
  }, [currentFiscalYear, setFilterValue]);

  useEffect(() => {
    if (loading) {
      setSummaryLoading(true);
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      setSummaryLoading(false);
    });
    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [loading, selectedFiscalYear, propertyPayments]);

  const fiscalYearOptions = useMemo(() => {
    return buildFiscalYearOptions(currentFiscalYear);
  }, [currentFiscalYear]);

  // "YYYY-MM" の収入年月が、選択中の会計年度に属するか。
  // 空 (aggregation_month 未設定) のレコードは年度で落とさず常に表示する。
  const inSelectedFiscalYear = useCallback(
    (billingMonth: string): boolean => {
      if (!billingMonth || !/^\d{4}-\d{2}$/.test(billingMonth)) return true;
      const yy = Number(billingMonth.slice(0, 4));
      const mm = Number(billingMonth.slice(5, 7));
      const fy = mm >= fiscalYearStartMonth ? yy : yy - 1;
      return fy === selectedFiscalYear;
    },
    [fiscalYearStartMonth, selectedFiscalYear],
  );

  const handleSearch = () => setSearchQuery(getFilterValues().q || "");
  const handleClearSearch = () => {
    resetFilter({ q: "", fiscalYear: String(currentFiscalYear) });
    setSearchQuery("");
    fiscalYearUserTouchedRef.current = false;
    setSelectedFiscalYear(currentFiscalYear);
  };

  // PaymentRecord → IncomeRow
  const rows: IncomeRow[] = useMemo(() => {
    return (propertyPayments as any[])
      .filter((p) => p && !p.deleted_at)
      .map((p) => {
        const rawItems: any[] = Array.isArray(p.items) ? p.items : [];
        const items: IncomeItem[] = rawItems.map((i) => ({
          name: String(i?.name || ""),
          amount: Number(i?.amount || 0),
        }));
        const amount = items.reduce((s, i) => s + i.amount, 0);
        // revenue_group: 先頭 item の category_id から導出 (同一 Unit なら揃う)
        const firstCatId = rawItems.length > 0 ? rawItems[0]?.category_id : "";
        const revenue_group = firstCatId
          ? categoryRevenueGroup[firstCatId] || ""
          : "";
        const billing_month = p.aggregation_month || "";
        // タイトル: BE の title 優先、無ければ "YYYY年MM月入金" にフォールバック。
        const monthLabel = formatBillingMonth(billing_month);
        const rawTitle = String(p.title || "").trim();
        const title = rawTitle || (monthLabel ? `${monthLabel}入金` : "-");
        const status = String(p.status || "");
        // 入金額: 案C(receipts)未実装のため暫定で status 由来 (入金済=請求額満額 / それ以外=null="-")。
        const paid_amount = status === "入金済" ? amount : null;
        return {
          id: String(p.payment_id),
          payment_id: p.payment_id,
          sort_key: p.sort_key,
          unit_id: p.unit_id,
          unit_name: p.landmark_name_snapshot || "",
          revenue_group,
          category_label: REVENUE_GROUP_LABEL[revenue_group] || "-",
          // 請求年月時点の契約者 snapshot を優先 (案C-b)。無ければ現行の active 契約から導出。
          tenant_name:
            p.contractor_name_snapshot ||
            (p.unit_id && tenantByUnitId[p.unit_id]) ||
            "",
          title,
          billing_month,
          status,
          amount,
          paid_amount,
          scheduled_date: p.scheduled_date || "",
          transfer_datetime: p.transfer_datetime || "",
          created_at: p.created_at || "",
          items,
        };
      });
  }, [propertyPayments, categoryRevenueGroup, tenantByUnitId]);

  // ── 収入タイトル押下で詳細プレビュー (PreviewDrawer + 入金詳細ページ) を開く ──
  const [detailRow, setDetailRow] = useState<IncomeRow | null>(null);
  const handleOpenDetail = useCallback((r: IncomeRow) => {
    setDetailRow(r);
  }, []);

  // ── 行の複数選択 + 一括削除 (入金管理ページ=PaymentHistoryTab と同じ挙動) ──
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [isDeleting, setIsDeleting] = useState(false);

  const handleRowSelection = useCallback((ids: any[]) => {
    setSelectedIds(new Set((ids || []).map(String)));
  }, []);

  // フィルタ / タブ / 検索の変更で一覧テーブルは key により remount されるため選択をリセット。
  useEffect(() => {
    setSelectedIds(new Set());
  }, [selectedGroup, searchQuery]);

  const handleOpenDeleteModal = useCallback(() => {
    if (selectedIds.size === 0) {
      showToast({ type: "warning", message: "削除する収入を選択してください" });
      return;
    }
    setDeleteModalOpen(true);
  }, [selectedIds, showToast]);

  const handleDelete = useCallback(async () => {
    setDeleteModalOpen(false);
    setIsDeleting(true);
    try {
      const targets = rows.filter((r) => selectedIds.has(r.id));
      await Promise.all(
        targets.map((r) =>
          dispatch(
            deletePayment({
              // 削除も client_id + sort_key で引かれる。section は unit 紐付け=unit_id、
              // 物件直入金 (その他=unit_id 不在) は propertyId を path に渡す。
              propertySectionId: r.unit_id || propertyId,
              paymentId: r.payment_id,
              sort_key: r.sort_key,
            }) as any,
          ).unwrap(),
        ),
      );
      setSelectedIds(new Set());
      // deletePayment.fulfilled は list のみ更新するため propertyList を再取得
      dispatch(fetchPaymentsByProperty({ propertyId }) as any);
      showToast({
        type: "success",
        message: `${targets.length}件の収入を削除しました`,
      });
    } catch {
      showToast({ type: "error", message: "削除に失敗しました" });
    } finally {
      setIsDeleting(false);
    }
  }, [rows, selectedIds, dispatch, propertyId, showToast]);

  // 選択中の会計年度に絞った行 (集計・カウント・一覧の母集合)
  const yearRows = useMemo(
    () => rows.filter((r) => inSelectedFiscalYear(r.billing_month)),
    [rows, inSelectedFiscalYear],
  );

  const countForGroup = useCallback(
    (groupKey: string): number =>
      yearRows.filter((r) => r.revenue_group === groupKey).length,
    [yearRows],
  );

  const filtered = useMemo(
    () =>
      yearRows.filter((r) => {
        if (selectedGroup !== ALL_TAB_KEY && r.revenue_group !== selectedGroup)
          return false;
        if (searchQuery && !r.unit_name.includes(searchQuery)) return false;
        return true;
      }),
    [yearRows, selectedGroup, searchQuery],
  );

  const summary = useMemo(() => {
    const now = new Date();
    const monthPrefix = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    // BE が読み取り時に旧ステータスを正規化するため、FE は新「入金済」のみ判定。
    const isPaid = (s: string) => s === "入金済";
    return {
      total: yearRows.reduce((s, r) => s + r.amount, 0),
      thisMonth: yearRows
        .filter((r) => r.billing_month === monthPrefix)
        .reduce((s, r) => s + r.amount, 0),
      paid: yearRows
        .filter((r) => isPaid(r.status))
        .reduce((s, r) => s + r.amount, 0),
      pending: yearRows
        .filter((r) => !isPaid(r.status))
        .reduce((s, r) => s + r.amount, 0),
    };
  }, [yearRows]);

  const columns: CustomColumnDef[] = useMemo(
    () => [
      {
        field: "billing_month",
        headerName: "請求年月",
        flex: 1,
        sortable: true,
        // 請求年月 (= aggregation_month)。下線テキスト押下で詳細ドロワーを開く (クリック起点)。
        renderCell: ({ row }) => {
          const r = row as IncomeRow;
          const label = formatBillingMonth(r.billing_month) || "-";
          return (
            <Box
              component="span"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDetail(r);
              }}
              sx={{
                minWidth: 0,
                width: "100%",
                fontSize: "14px",
                textDecoration: "underline",
                cursor: "pointer",
                color: "inherit",
                "&:hover": { color: "#0b9dbd" },
              }}
            >
              <TableEllipsisText text={label} />
            </Box>
          );
        },
      },
      {
        field: "tenant_name",
        headerName: "テナント名",
        flex: 1.2,
        sortable: true,
        renderCell: ({ value }) => (
          <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
            <TableEllipsisText text={String(value || "") || "-"} />
          </Box>
        ),
      },
      {
        field: "items",
        headerName: "明細項目",
        flex: 1.4,
        sortable: false,
        tooltipValueGetter: ({ row }) => {
          const r = row as IncomeRow;
          return Array.from(
            new Set(r.items.map((i) => i.name).filter(Boolean)),
          ).join(", ");
        },
        // 明細項目 = items の科目名チップ。入金管理一覧 (PaymentHistoryTab) の
        // 「支払項目」と同じグレーチップを採用。同一科目複数行は科目名 distinct。
        renderCell: ({ row }) => {
          const r = row as IncomeRow;
          const names = Array.from(
            new Set(r.items.map((i) => i.name).filter(Boolean)),
          );
          if (names.length === 0) {
            return (
              <Box component="span" sx={{ color: "#999", fontSize: 14 }}>
                -
              </Box>
            );
          }
          return (
            <Box
              sx={{
                display: "flex",
                flexWrap: "nowrap",
                alignItems: "center",
                gap: 0.5,
                py: 0.5,
                minWidth: 0,
                width: "100%",
                overflow: "hidden",
              }}
            >
              {names.map((n, i) => (
                <Box
                  key={`${i}-${n}`}
                  sx={{
                    bgcolor: "#F0F0F0",
                    display: "inline-flex",
                    alignItems: "center",
                    height: 24,
                    px: 1,
                    borderRadius: "4px",
                    fontSize: 12,
                    color: "#3E3E3E",
                    flexShrink: 0,
                    whiteSpace: "nowrap",
                  }}
                >
                  {n}
                </Box>
              ))}
            </Box>
          );
        },
      },
      {
        field: "amount",
        headerName: "請求額",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => (
          <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
            <TableEllipsisText text={fmt(Number(value) || 0)} />
          </Box>
        ),
      },
      {
        field: "paid_amount",
        headerName: "入金額",
        flex: 1,
        sortable: true,
        // 入金額: 案C(receipts)未実装のため暫定で status 由来 (入金済=満額 / それ以外="-")。
        renderCell: ({ row }) => {
          const r = row as IncomeRow;
          return (
            <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
              <TableEllipsisText
                text={r.paid_amount != null ? fmt(r.paid_amount) : "-"}
              />
            </Box>
          );
        },
      },
      {
        field: "scheduled_date",
        headerName: "支払期日",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => {
          const v = value ? String(value) : "";
          const d = v ? dayjs(v) : null;
          const text = d && d.isValid() ? d.format("YYYY/MM/DD") : "-";
          return (
            <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
              <TableEllipsisText text={text} />
            </Box>
          );
        },
      },
      {
        field: "transfer_datetime",
        headerName: "入金日",
        flex: 1,
        sortable: true,
        renderCell: ({ value }) => {
          const v = value ? String(value) : "";
          const d = v ? dayjs(v) : null;
          const text = d && d.isValid() ? d.format("YYYY/MM/DD") : "-";
          return (
            <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
              <TableEllipsisText text={text} />
            </Box>
          );
        },
      },
      {
        field: "status",
        headerName: "ステータス",
        // 中身は固定 140px のセレクト。列数が増えても潰れて隣に被らないよう固定幅にする。
        width: 160,
        sortable: true,
        disableTooltip: true,
        // 入金管理ページと同じインラインセレクト。選択で即更新 (楽観的 + 再取得)。
        renderCell: ({ row }) => {
          const r = row as IncomeRow;
          return (
            <Box sx={{ width: "140px", minWidth: "140px", flexShrink: 0 }}>
              <CustomSelect
                key={`${r.payment_id}_${statusMap[r.payment_id] || r.status}`}
                name={`status_${r.payment_id}`}
                options={statusOptions.map((o) => ({
                  label: o.label,
                  value: o.value,
                }))}
                width="140px"
                XsWidth="140px"
                fontSize="14px"
                defaultValue={statusMap[r.payment_id] || r.status || ""}
                sx={{
                  width: "140px",
                  minWidth: "140px",
                  flexShrink: 0,
                }}
                onChange={(v) =>
                  handleInlineStatusChange(
                    r.payment_id,
                    r.unit_id,
                    r.sort_key,
                    String(v),
                  )
                }
              />
            </Box>
          );
        },
      },
      {
        field: "created_at",
        headerName: "登録日時",
        flex: 1.1,
        sortable: true,
        renderCell: ({ value }) => {
          const v = value ? String(value) : "";
          const d = v ? dayjs(v) : null;
          const text = d && d.isValid() ? d.format("YYYY/MM/DD HH:mm") : "-";
          return (
            <Box sx={{ minWidth: 0, width: "100%", fontSize: "14px" }}>
              <TableEllipsisText text={text} />
            </Box>
          );
        },
      },
    ],
    [statusOptions, statusMap, handleInlineStatusChange, handleOpenDetail],
  );

  const cardTabs = useMemo(
    () => [
      {
        key: ALL_TAB_KEY,
        label: "すべて",
        count: yearRows.length,
        hasNotification: false,
      },
      ...REVENUE_GROUPS.map((g) => ({
        key: g.key,
        label: g.label,
        count: countForGroup(g.key),
        hasNotification: false,
      })),
    ],
    [yearRows.length, countForGroup],
  );

  const cardTabData = useMemo(
    () =>
      Object.fromEntries(
        [ALL_TAB_KEY, ...REVENUE_GROUPS.map((g) => g.key)].map((key) => [
          key,
          { data: [], pagination: { total: 0, page: 1, limit: 50 } },
        ]),
      ),
    [],
  );

  const activeTabIndex = useMemo(() => {
    if (selectedGroup === ALL_TAB_KEY) return 0;
    const idx = REVENUE_GROUPS.findIndex((g) => g.key === selectedGroup);
    return idx < 0 ? 0 : idx + 1;
  }, [selectedGroup]);

  const handleCardTabChange = useCallback((idx: number) => {
    if (idx === 0) {
      setSelectedGroup(ALL_TAB_KEY);
      return;
    }
    const g = REVENUE_GROUPS[idx - 1];
    setSelectedGroup(g ? g.key : ALL_TAB_KEY);
  }, []);
  const tableResetKey = `${selectedGroup}|${searchQuery}|${perPage}`;
  const tableDataReady = !loading && !budgetCategoriesLoading;
  const desktopTable = useMemo(
    () => (
      <CustomTable
        rows={tableDataReady ? filtered : []}
        columns={columns}
        initialSort={{ field: "created_at", direction: "desc" }}
        outlined={false}
        minHeight="auto"
        isLoading={!tableDataReady}
        enableCheckboxSelection
        onRowSelection={handleRowSelection}
        pageSize={perPage}
        pageResetKey={tableResetKey}
        noDataMessage="収入が登録されていません"
        noDataIcon={
          <ReceiptIcon
            sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
          />
        }
      />
    ),
    [
      filtered,
      columns,
      tableDataReady,
      handleRowSelection,
      perPage,
      tableResetKey,
    ],
  );

  return (
    <Box sx={{ pb: 2 }}>
      <Box sx={{ mb: 0 }}>
        <CustomSectionTitle title="収入管理" />
      </Box>
      {/* 年度切替: セクションタイトル直下に独立配置 (支出と同じレイアウト)。
          変更で表示対象の会計年度が切り替わる。 */}
      <Box sx={{ mt: 1.5, mb: 1, width: 140 }}>
        <CustomSingleSelect
          label=""
          name="fiscalYear"
          control={controlFilter}
          errors={filterErrors}
          options={fiscalYearOptions}
          onChangeValue={(val) => {
            const yr = Number(val);
            if (!Number.isNaN(yr) && Number.isFinite(yr)) {
              fiscalYearUserTouchedRef.current = true;
              setSummaryLoading(true);
              setSelectedFiscalYear(yr);
            }
          }}
          labelWidth="0px"
          inputWidth="100%"
          inputWidthSp="100%"
          hasSpacing={false}
        />
      </Box>

      {/* サマリー */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          // 金額が長いカードだけ内容に追従して広がる (均等割りにしない)。
          // 各カードは内容幅、入り切らなければ次行へ折返す。
          gap: 1,
          mb: 1,
          width: "100%",
          "& > *": { flex: "0 0 auto", minWidth: "160px" },
          "& .summary-card-value": { whiteSpace: "nowrap" },
        }}
      >
        <CustomSummaryCard
          label="累計収入"
          value={fmt(summary.total ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="当月収入"
          value={fmt(summary.thisMonth ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="入金済"
          value={fmt(summary.paid ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
        <CustomSummaryCard
          label="入金予定"
          value={fmt(summary.pending ?? 0)}
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
        />
      </Box>

      {/* カテゴリフィルタ (CardTab) — revenue_group の 3 区分 */}
      <Box sx={{ mb: 1 }}>
        <CardTab
          loading={effectiveSummaryLoading}
          loadingDelayMs={150}
          tabs={cardTabs}
          tabData={cardTabData}
          activeTab={activeTabIndex}
          onTabChange={handleCardTabChange}
        />
      </Box>

      {/* 検索・登録 */}
      <Box
        sx={{
          display: "flex",
          gap: 1.25,
          mb: 1,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <Box sx={{ minWidth: 240, flex: 1, maxWidth: 380 }}>
          <CustomTextInput
            label=""
            name="q"
            placeholder="明細名で検索"
            register={registerFilter}
            errors={filterErrors}
            inputWidth="100%"
            inputWidthSp="100%"
            showSearchIconStart
            search
            hasSpacing={false}
          />
        </Box>
        <CustomButton
          label="検索"
          buttonCategory="search"
          onClick={handleSearch}
        />
        <CustomButton
          label="クリア"
          buttonCategory="clear"
          onClick={handleClearSearch}
        />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#3e3e3e",
              whiteSpace: "nowrap",
            }}
          >
            表示件数
          </Typography>
          <Box sx={{ minWidth: 70 }}>
            <CustomSingleSelect
              label=""
              name="perPage"
              control={perPageControl}
              errors={{}}
              options={PER_PAGE_OPTIONS}
              inputWidth="80px"
              inputWidthSp="100%"
              hasSpacing={false}
              onChangeValue={(value) => {
                const next = Number(value);
                if (Number.isFinite(next)) setPerPage(next);
              }}
            />
          </Box>
        </Box>
        {/* 「その他」収入 (Unit 非紐付け) の登録。貸室・その他収入は各区画の入金履歴から。 */}
        <Can action="expense:edit">
          <CustomButton
            label="収入登録"
            onClick={() => setCreateOpen(true)}
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
          />
        </Can>
        <Can action="expense:delete">
          <CustomButton
            label="削除"
            buttonCategory="delete"
            onClick={handleOpenDeleteModal}
            disabled={isDeleting || selectedIds.size === 0}
          />
        </Can>
      </Box>

      {/* 一覧テーブル (登録日 降順) */}
      {isMobile ? (
        <MobileTable
          key={`${selectedGroup}|${searchQuery}|${perPage}`}
          rows={filtered}
          // タイトル列(請求年月)は renderTitle で出すので本文からは除外。
          columns={columns.filter((c) => c.field !== "billing_month")}
          checkBoxOpen
          selectedIds={Array.from(selectedIds)}
          onRowSelection={handleRowSelection}
          pageSize={perPage}
          noDataMessage="収入が登録されていません"
          noDataIcon={
            <ReceiptIcon
              sx={{ fontSize: 40, color: colors.border.light, mb: 1 }}
            />
          }
          handlePropertyClick={(row) => handleOpenDetail(row as IncomeRow)}
          renderTitle={(row) => (
            <Typography
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                handleOpenDetail(row as IncomeRow);
              }}
              sx={{
                fontSize: "14px",
                fontWeight: 500,
                textDecoration: "underline",
                cursor: "pointer",
                color: "#3e3e3e",
              }}
            >
              {formatBillingMonth((row as IncomeRow).billing_month) || "-"}
            </Typography>
          )}
        />
      ) : (
        desktopTable
      )}

      {/* 収入登録モーダル (段階式: カテゴリ → 区画 → 科目)。共有 IncomeCreateDrawer。
          初期カテゴリは選択中 CardTab。「すべて」タブのときは未選択で開く。 */}
      <IncomeCreateDrawer
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        propertyId={propertyId}
        initialCategory={
          selectedGroup !== ALL_TAB_KEY ? selectedGroup : undefined
        }
        onCreated={() => {
          dispatch(fetchPaymentsByProperty({ propertyId }) as any);
        }}
      />

      {/* 収入詳細プレビュー: PreviewDrawer + 入金詳細ページ埋め込み。収入タイトル押下で開く。
          展開で /payments/:id へ。パンくずは収入タブ起点 (収支 > 収入)。
          「その他」収入 (Unit 非紐付け) は propertySectionId を物件IDにフォールバック。 */}
      <PaymentDetailDrawer
        open={Boolean(detailRow)}
        onClose={() => setDetailRow(null)}
        paymentId={detailRow?.payment_id || null}
        propertySectionId={detailRow?.unit_id || propertyId}
        propertyId={propertyId}
        propertyName={propertyName}
        unitName={detailRow?.unit_name}
        fromSource="income"
        onUpdated={() => {
          dispatch(fetchPaymentsByProperty({ propertyId }) as any);
        }}
      />

      {/* 削除確認モーダル (入金管理ページと同じ deleteModal) */}
      <CustomModal
        title="削除の確認"
        openModal={deleteModalOpen}
        handleCloseModal={() => setDeleteModalOpen(false)}
        bodyText=""
        hideCloseButton
        modalType="deleteModal"
      >
        <Box>
          <Typography sx={{ fontSize: "14px", color: "#666" }}>
            選択した{selectedIds.size}件の収入を削除してもよろしいでしょうか？
            <br />
            予実管理の集計からも該当の金額が差し引かれます
            (内訳行は履歴として残ります)。
            <br />
            削除されたデータは復元できません。
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center" }}>
          <CustomButton
            label="戻る"
            onClick={() => setDeleteModalOpen(false)}
            buttonCategory="cancelModal"
          />
          <Can action="expense:delete">
            <CustomButton
              label="削除"
              onClick={handleDelete}
              buttonCategory="deleteModal"
              disabled={isDeleting}
            />
          </Can>
        </Box>
      </CustomModal>
    </Box>
  );
};

export default IncomeListTab;
